import axios, { AxiosError, AxiosHeaders } from 'axios'
import tenantConfig from 'config/tenant'
import { secureGetItem, secureSetItem } from 'utils/secureStorage'

const baseUrl = process.env.REACT_APP_BASE_URL ?? ''
export const getAccessToken = () => {
  return secureGetItem(`${tenantConfig.storagePrefix}_authToken`)
}

const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken()

    if (config.headers.Authorization !== false && token) {
      ;(config.headers as AxiosHeaders).set(
        'Authorization',
        token ? `Bearer ${token}` : ''
      )
    } else {
      // eslint-disable-next-line no-param-reassign
      delete config.headers.Authorization
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config
    const status = error.response?.status
    const data = error.response?.data as
      | { errorCode?: string; error?: string; message?: string }
      | undefined

    const isAuthEndpoint = originalRequest.url?.includes('/auth/')
    const refreshToken = secureGetItem(
      `${tenantConfig.storagePrefix}_refreshToken`
    )

    if (
      status === 401 &&
      !originalRequest.retry &&
      !isAuthEndpoint &&
      refreshToken
    ) {
      originalRequest.retry = true
      try {
        const response = await axios.post(
          `${baseUrl}/auth/refresh-token?refreshToken=${encodeURIComponent(
            refreshToken
          )}`
        )
        const newAccessToken = response?.data?.content?.token ?? ''
        secureSetItem(`${tenantConfig.storagePrefix}_authToken`, newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return await axiosInstance(originalRequest)
      } catch (refreshError) {
        localStorage.clear()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    if (status === 429) {
      // eslint-disable-next-line no-param-reassign
      error.message =
        data?.message ??
        'Too many requests. Please wait a moment before trying again.'
    } else if (status === 403 && data?.errorCode === 'IP_BLOCKED') {
      // eslint-disable-next-line no-param-reassign
      error.message =
        data?.message ??
        'Access has been blocked from this network. Please try again later or contact support.'
    } else if (status === 403 && data?.error === 'ACCOUNT_INACTIVE') {
      const inactiveMessage =
        data?.message ?? 'Your account has been deactivated.'
      // eslint-disable-next-line no-param-reassign
      error.message = inactiveMessage
      window.dispatchEvent(
        new CustomEvent('account-inactive', {
          detail: { message: inactiveMessage }
        })
      )
    }
    throw error
  }
)

export default axiosInstance
